import { ParsedUrlQuery } from 'querystring';
import { isEqual } from 'lodash';

import {
  FilterableField,
  FilterableFieldSetting,
  FilterableFieldSettings,
} from 'hooks/useGlobalSearch/useGlobalSearchInternal';
import { TSearchQueryObject } from 'lib/models/search-page';

export type TQueryParams = Record<
  string,
  Array<string> | string | boolean | number | null
>;

export function getTypeFilterQueryParams({
  filters,
  query,
}: {
  filters: Array<FilterableField>;
  query: ParsedUrlQuery;
}) {
  const newQuery: Record<string, string | string[] | undefined> = query;
  // If the search page slug is present, we need to keep it in the query for seo pages
  if (query.searchPageSlug) {
    newQuery.searchPageSlug = query.searchPageSlug;
  }
  try {
    Object.keys(newQuery).forEach((newQueryKey) => {
      if (
        Object.entries(FilterableFieldSettings).find(
          ([_, value]) =>
            value?.param === newQueryKey || value?.subParam === newQueryKey
        ) &&
        !filters.find(
          (f) =>
            FilterableFieldSettings[f]?.param === newQueryKey ||
            FilterableFieldSettings[f]?.subParam === newQueryKey
        )
      ) {
        delete newQuery[newQueryKey];
      }
    });
  } catch (e) {
    console.error('Error parsing query params', e);
  }

  filters.forEach((filter) => {
    const { param, subParam } = FilterableFieldSettings[filter];
    if (query[param]) {
      newQuery[param] = query[param];
    }
    if (subParam && query[subParam]) {
      newQuery[subParam] = query[subParam];
    }
  });

  return newQuery;
}

export function getOpportunityFilterQueryParams({
  filters,
  query,
}: {
  filters: Array<FilterableField>;
  query: ParsedUrlQuery;
}) {
  const newQuery: TSearchQueryObject = {};
  filters.forEach((filter) => {
    const { param, subParam } = FilterableFieldSettings[filter];
    if (query[param]) {
      newQuery[param] = query[param] as string | string[];
    }
    if (subParam && query[subParam]) {
      newQuery[subParam] = query[subParam] as string | string[];
    }
  });

  return newQuery;
}

export const hasFilterQueryChanged = ({
  oldQuery,
  newQuery,
  filters,
}: {
  oldQuery: ParsedUrlQuery | null;
  newQuery: ParsedUrlQuery;
  filters: Array<FilterableFieldSetting>;
}) => {
  if (!oldQuery) return true;
  if (oldQuery.type !== newQuery.type) return true;

  return filters.some((filter) => {
    const { param, subParam } = filter;
    return (
      !isEqual(oldQuery[param], newQuery[param]) ||
      (subParam && !isEqual(oldQuery[subParam], newQuery[subParam]))
    );
  });
};
